import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Eye, Loader2 } from 'lucide-react';
import { StaffDeal } from '@/hooks/useStaffDeals';
import { useLanguage } from '@/contexts/LanguageContext';
import { useAuth } from '@/contexts/AuthContext';
import type { StaffUserContext } from '@/lib/staffDealActions';
import * as A from '@/lib/staffDealActions';

interface Props {
  deal: StaffDeal;
  onViewDeal?: (id: string) => void;
  onRefresh?: () => void;
}

type ActionDef = {
  key: string;
  label: { en: string; fr: string };
  variant?: 'default' | 'outline' | 'destructive';
  fn: (ctx: { dealId: string; lang: 'en' | 'fr'; userCtx: StaffUserContext }) => Promise<void>;
};

// Libellé informatif (pas de bouton) pour les étapes désormais automatiques
type InfoDef = { en: string; fr: string };

function resolveActions(deal: StaffDeal): ActionDef[] {
  const stage = (deal as any).stage as string | undefined;
  const status = deal.status;
  const key = stage ?? status;

  switch (key) {
    case 'submission':
    case 'pending_triage':
    case 'pending_review':
      return [
        { key: 'preEval', label: { en: 'Pre-Evaluate', fr: 'Pré-évaluer' }, fn: A.preEvaluate },
        { key: 'reqInfo', label: { en: 'Request Info', fr: 'Demander info' }, variant: 'outline', fn: A.requestMoreInfo },
        { key: 'reject', label: { en: 'Reject', fr: 'Rejeter' }, variant: 'destructive', fn: A.rejectDeal },
      ];

    case 'pre_evaluation':
      return [
        { key: 'genCIM', label: { en: 'Generate CIM', fr: 'Générer CIM' }, fn: A.generateCIM },
      ];

    // Anciens noms conservés en alias, au cas où des documents pas encore
    // migrés porteraient encore ces valeurs.
    case 'evaluation':
    case 'evaluation_in_progress':
    case 'cim_ready_for_internal_review':
    case 'cim_creation':
      return [
        { key: 'sendCIM', label: { en: 'Send for Approval', fr: 'Envoyer pour approbation' }, fn: A.sendCIMToSeller },
      ];

    // payment_pending / published / lender_submission : automatiques via le
    // paiement Stripe (webhook -> onApprovalFeePaid). Pas de bouton d'action
    // manuelle ici, sauf un filet de sécurité si le matching a échoué.
    case 'payment_pending':
      return [];

    case 'deal_monitor': // alias legacy
    case 'published':
      return [
        { key: 'retryMatch', label: { en: 'Retry Lender Matching', fr: 'Relancer distribution prêteurs' }, variant: 'outline', fn: A.retryLenderMatching },
      ];

    case 'lender_submission':
      // NDA + accès CIM prêteurs : automatique (email envoyé par
      // LenderMatchingService.onDealApprovalPaid), aucune action staff ici.
      return [];

    case 'nda_signed':
      return [
        { key: 'share', label: { en: 'Share with Buyer', fr: 'Partager avec acheteur' }, fn: A.shareWithBuyer },
        { key: 'meet', label: { en: 'Schedule Meeting', fr: 'Planifier réunion' }, variant: 'outline', fn: A.scheduleMeeting },
      ];

    case 'buyer_contact':
      return [
        { key: 'meet', label: { en: 'Schedule Meeting', fr: 'Planifier réunion' }, fn: A.scheduleMeeting },
      ];

    case 'loi_pending': // alias legacy
    case 'first_meeting':
      return [
        { key: 'genLOI', label: { en: 'Generate LOI', fr: 'Générer LOI' }, fn: A.generateLOI },
        { key: 'track', label: { en: 'Track Signature', fr: 'Suivre signature' }, variant: 'outline', fn: A.trackLOISignature },
      ];

    case 'loi_signed':
      return [
        { key: 'track', label: { en: 'Track Signature', fr: 'Suivre signature' }, variant: 'outline', fn: A.trackLOISignature },
      ];

    case 'closing':
      return [
        { key: 'invoice', label: { en: 'Generate Invoice', fr: 'Générer facture' }, fn: A.generateInvoice },
        { key: 'closed', label: { en: 'Mark Closed', fr: 'Marquer clôturé' }, variant: 'outline', fn: A.markClosed },
      ];

    default:
      return [];
  }
}

export const StaffDealRowActions: React.FC<Props> = ({ deal, onViewDeal, onRefresh }) => {
  const { language } = useLanguage();
  const { user } = useAuth();
  const lang = (language === 'fr' ? 'fr' : 'en') as 'en' | 'fr';
  const [busy, setBusy] = useState<string | null>(null);
  const actions = resolveActions(deal);

  const userCtx: StaffUserContext = {
    uid: user?.id ?? 'unknown',
    email: user?.email ?? null,
    role: (user as any)?.role ?? 'staff',
  };

  const run = async (a: ActionDef) => {
    setBusy(a.key);
    try {
      await a.fn({ dealId: deal.id, lang, userCtx });
      onRefresh?.();
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="flex items-center gap-1.5 flex-wrap">
      <Button
        size="sm"
        variant="ghost"
        className="text-gray-400 hover:text-white"
        onClick={() => onViewDeal?.(deal.id)}
        title={lang === 'fr' ? 'Voir détail' : 'View detail'}
      >
        <Eye className="w-4 h-4" />
      </Button>

      {actions.map((a) => (
        <Button
          key={a.key}
          size="sm"
          variant={a.variant ?? 'default'}
          disabled={busy !== null}
          onClick={() => run(a)}
          className={
            a.variant === 'default' || !a.variant
              ? 'bg-indigo-600 hover:bg-indigo-700 text-white'
              : a.variant === 'outline'
                ? 'border-gray-700 text-gray-300 hover:bg-gray-800 hover:text-white'
                : ''
          }
        >
          {busy === a.key ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : a.label[lang]}
        </Button>
      ))}

      {actions.length === 0 && (
        <span className="text-xs text-gray-500 italic">
          {(deal as any).stage === 'payment_pending'
            ? (lang === 'fr' ? 'En attente du paiement du vendeur' : 'Awaiting seller payment')
            : (lang === 'fr' ? 'Aucune action' : 'No action')}
        </span>
      )}
    </div>
  );
};